import { SearchIcon } from '@app/components/icons/search';
import { DownloadFile } from '@app/routes/content/routes/recordings/components/icons';
import {
  selectRecordingTranscriptionStatus,
  selectTranscriptSearchQuery,
} from '@app/routes/content/routes/recordings/selector';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { Menu, Transition } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { Fragment, useCallback, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useRecordingAnalytics } from '../../../../../../hooks';
import { useEffectiveTranscript } from './hooks/use-effective-transcript';
import { triggerFileDownload } from './utils/effective-transcript';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Header for the EditTranscript panel.
 *
 * Search input   → filters visible transcript segments by keyword.
 *
 * Download VTT   → downloads the effective transcript as a WebVTT file with
 *                  remapped edit-timeline timestamps.
 *
 * Download TXT   → downloads the effective transcript as plain text
 *                  (one segment group per line, corrections applied,
 *                   cut words excluded, no timestamps).
 */
export const EditTranscriptHeader = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { trackRecordingEvent } = useRecordingAnalytics();
  const inputRef = useRef<HTMLInputElement>(null);

  const transcriptionStatus = useAppSelector(
    selectRecordingTranscriptionStatus,
  );
  const searchQuery = useAppSelector(selectTranscriptSearchQuery);
  const isTranscriptReady = transcriptionStatus === 'ready';

  // Effective transcript — corrections applied + cut words removed
  const { vttContent, textContent, editTitle } = useEffectiveTranscript();

  // ── Search ────────────────────────────────────────────────────────────────

  const debouncedDispatch = useDebouncedCallback((query: string) => {
    dispatch({
      type: '[recording] update transcript search query',
      payload: { searchQuery: query },
    });
  }, SEARCH_DEBOUNCE_MS);

  const handleSearchChange = useCallback(() => {
    if (inputRef.current) {
      debouncedDispatch(inputRef.current.value);
    }
  }, [debouncedDispatch]);

  // ── Downloads ─────────────────────────────────────────────────────────────

  const handleDownloadVtt = () => {
    trackRecordingEvent('ch_recording_editor_transcript_downloaded_vtt');
    triggerFileDownload(vttContent, `${editTitle}.vtt`, 'text/vtt');
  };

  const handleDownloadTxt = () => {
    trackRecordingEvent('ch_recording_editor_transcript_downloaded_txt');
    triggerFileDownload(textContent, `${editTitle}.txt`, 'text/plain');
  };

  return (
    <div className="flex items-center gap-2 pb-3 z-[1] px-4 shrink-0">
      {/* Search */}
      <div className="flex-1 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          defaultValue={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search"
          className="
            block w-full pl-10 pr-3 py-2 bg-spring-neutral-base border border-spring-neutral-b4
            rounded-lg text-sm placeholder-gray-400 text-white h-9
            focus:outline-none focus:border-spring-primary
          "
        />
      </div>

      {/* Download menu — VTT and TXT both use the effective transcript */}
      <Menu as="div" className="relative inline-block text-left z-10">
        <Menu.Button className="flex items-center text-sm font-medium text-spring-neutral-b0 rounded-10 hover:bg-spring-neutral-b0/10 transition-colors overflow-hidden">
          <DownloadFile className="size-5" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 max-w-80 w-max origin-top-right rounded-lg bg-spring-neutral-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-600">
            <div className="p-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${
                      active ? 'bg-gray-700' : ''
                    } group flex w-full items-center rounded-md px-2 py-2 text-sm font-bold text-white hover:bg-gray-700 transition-colors`}
                    onClick={handleDownloadVtt}
                    disabled={!isTranscriptReady}
                    title={
                      !isTranscriptReady
                        ? t(
                            'content-recording.content-menu.transcript-not-ready',
                          )
                        : t('content-recording.content-menu.download-vtt')
                    }
                    data-testid="edit-transcript-download-vtt-button"
                  >
                    {t('content-recording.content-menu.download-vtt')}
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${
                      active ? 'bg-gray-700' : ''
                    } group flex w-full items-center rounded-md px-2 py-2 text-sm font-bold text-white hover:bg-gray-700 transition-colors`}
                    onClick={handleDownloadTxt}
                    disabled={!isTranscriptReady}
                    title={
                      !isTranscriptReady
                        ? t(
                            'content-recording.content-menu.transcript-not-ready',
                          )
                        : t('content-recording.content-menu.download-txt')
                    }
                    data-testid="edit-transcript-download-txt-button"
                  >
                    {t('content-recording.content-menu.download-txt')}
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};
